import type React from 'react';
import { useRef } from 'react';
import { TIMELINE_EVENT } from '../../../constants/frame-timeline-v4';
import type { PointerDragT, TimelineGestureDetailT } from '../../../types/frame-timeline-v4';
import { cellAnchorAt, dragTargetAt, elementOf, frameFromAnchor, frameUnderPointer } from '../utils/pointer-dom';
export function useTimelinePointerEvents(stage: EventTarget, containerRef: React.RefObject<HTMLDivElement>) {
  const dragRef = useRef<PointerDragT | null>(null);
  const emit = (type: string, detail: TimelineGestureDetailT) => {
    stage.dispatchEvent(new CustomEvent(type, {
      detail
    }));
  };
  const emitPointer = (type: string, e: React.MouseEvent<HTMLDivElement>) => {
    emit(type, {
      target: elementOf(e.target),
      frame: frameUnderPointer(e.clientX, e.clientY),
      clientX: e.clientX
    });
  };
  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button > 0 || dragRef.current) {
      return;
    }
    const target = dragTargetAt(e.target);
    if (!target) {
      emitPointer(TIMELINE_EVENT.POINTER_DOWN, e);
      return;
    }
    e.preventDefault();
    containerRef.current?.setPointerCapture(e.pointerId);
    const anchor = cellAnchorAt(e.clientX, e.clientY);
    dragRef.current = {
      target,
      moved: false,
      currentFrame: anchor?.frame ?? frameUnderPointer(e.clientX, e.clientY),
      anchor: anchor && anchor.width > 0 ? anchor : null
    };
  };
  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag) {
      emitPointer(TIMELINE_EVENT.POINTER_MOVE, e);
      return;
    }
    const frame = drag.anchor !== null ? frameFromAnchor(e.clientX, drag.anchor) : frameUnderPointer(e.clientX, e.clientY);
    if (frame !== null) {
      drag.currentFrame = frame;
    }
    if (!drag.moved) {
      drag.moved = true;
      emit(TIMELINE_EVENT.DRAG_START, {
        target: drag.target,
        frame: drag.currentFrame,
        clientX: e.clientX
      });
    }
    emit(TIMELINE_EVENT.DRAG, {
      target: drag.target,
      frame: drag.currentFrame,
      clientX: e.clientX
    });
  };
  const onPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag) {
      emitPointer(TIMELINE_EVENT.POINTER_UP, e);
      return;
    }
    dragRef.current = null;
    containerRef.current?.releasePointerCapture(e.pointerId);
    if (drag.moved) {
      emit(TIMELINE_EVENT.DRAG_END, {
        target: drag.target,
        frame: drag.currentFrame,
        clientX: e.clientX
      });
    }
  };
  const onPointerEnter = (e: React.PointerEvent<HTMLDivElement>) => {
    emitPointer(TIMELINE_EVENT.POINTER_ENTER, e);
  };
  const onPointerLeave = (e: React.PointerEvent<HTMLDivElement>) => {
    emitPointer(TIMELINE_EVENT.POINTER_LEAVE, e);
  };
  const onClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.detail === 2) {
      emitPointer(TIMELINE_EVENT.DOUBLE_CLICK, e);
    }
  };
  const onContextMenu = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    emitPointer(TIMELINE_EVENT.RIGHT_CLICK, e);
  };
  return {
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerEnter,
    onPointerLeave,
    onClick,
    onContextMenu
  };
}